import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import {
  ModalLarge,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  ModalButton,
  ModalContent,
} from 'components/patterns/Modal'
import { Table, Icon, Header } from 'components/patterns'
import { ChecTooltip } from 'components/chec'
import strings from 'utils/strings'
import { capitalizeEachWord, classNames } from 'utils/utils'
import { Link } from 'react-router-dom'
import styled from '@emotion/styled'
import { prop, sortBy } from 'ramda'
import { makeKeepOrRemoveSwitchTableData } from '../utils'

const KeepOrRemoveSwitches = ({
  isOpen,
  onClickCancel,
  onClickContinue,
  removedSwitches,
  onGoingSet,
  serialNumber,
}) => {
  const [dataSource, setDataSource] = useState(null)
  const totalRemovedSwitches = removedSwitches.length

  useEffect(() => {
    if (!dataSource && totalRemovedSwitches) {
      const data = makeKeepOrRemoveSwitchTableData(removedSwitches, onGoingSet)
      setDataSource(data)
    }
  }, [dataSource, removedSwitches, onGoingSet, totalRemovedSwitches])

  const addRemoveOnClick = (panelNumber) => {
    const nextData = dataSource.map((data) => {
      if (data.panelNumber === panelNumber) {
        return {
          ...data,
          isMarkedForAddition: !data.isMarkedForAddition,
          isMarkedForDeletion: !data.isMarkedForDeletion,
        }
      }
      return data
    })

    setDataSource(nextData)
  }

  if (!isOpen) return null

  return (
    <ModalLarge>
      <ModalHeader>
        <Header type="h2">{strings.keep_switches_on(serialNumber)}</Header>
        <ModalCloseButton onClick={onClickCancel} />
      </ModalHeader>
      <ModalBody>
        <ModalContent>
          <Blurb>{strings.remove_of_keep_blurb(serialNumber)}</Blurb>
          <Table
            columns={[
              {
                title: null,
                key: 'panelNumber',
                dataIndex: 'isMarkedForDeletion',
                render: (_, record) => (
                  <AddRemoveCell {...record} handleClick={addRemoveOnClick} />
                ),
                align: 'center',
                width: 40,
                className: 'custom-column',
              },
              {
                title: strings.part_no,
                dataIndex: 'panelNumber',
                key: 'panelNumber',
              },
              {
                title: strings.description,
                dataIndex: 'description',
                key: 'description',
              },
              {
                title: capitalizeEachWord(strings.type),
                dataIndex: 'type',
                key: 'panelNumber',
                render: (type) => <>{capitalizeEachWord(type)}</>,
              },
              {
                title: strings.associated_features,
                dataIndex: 'associatedFeatures',
                key: 'associatedFeatures',
                render: (associatedFeatures) =>
                  associatedFeatures.map((feature) => (
                    <Link
                      key={feature.uri}
                      style={{ display: 'block' }}
                      to={feature.uri}
                      target="_blank"
                    >
                      {strings.feature_display_with_revision({
                        partNumber: feature.partNumber,
                        revision: feature.revision,
                      })}
                    </Link>
                  )),
              },
            ]}
            dataSource={sortBy(prop('panelNumber'))(dataSource || [])}
          />
        </ModalContent>
      </ModalBody>
      <ModalFooter>
        <ModalButton type="secondary" onClick={onClickCancel}>
          {strings.cancel}
        </ModalButton>
        <ModalButton type="primary" onClick={() => onClickContinue(dataSource)}>
          {strings.confirm_selected_switches}
        </ModalButton>
      </ModalFooter>
    </ModalLarge>
  )
}

KeepOrRemoveSwitches.propTypes = {
  isOpen: PropTypes.bool,
  onClickCancel: PropTypes.func,
  onClickContinue: PropTypes.func,
  removedSwitches: PropTypes.array,
  onGoingSet: PropTypes.array,
  serialNumber: PropTypes.string,
}

export default KeepOrRemoveSwitches

const AddRemoveCell = ({
  isMarkedForDeletion,
  isMarkedForAddition,
  panelNumber,
  handleClick,
}) => {
  const [isTooltipVisible, setIsTooltipVisible] = useState(false)

  const iconProps = () => {
    if (isMarkedForDeletion) {
      return {
        icon: 'plus-circle',
        style: {
          color: '#0b2e6b',
          fontSize: '20px',
        },
        fill: 'filled',
        hoverFill: 'outlined',
      }
    }

    return {
      icon: 'plus-circle',
      style: {
        color: '#4f5764',
        fontSize: '20px',
        transform: 'rotate(45deg)',
      },
      fill: 'outlined',
      hoverFill: 'filled',
    }
  }

  const stateIndicatorClassNames = classNames('state-indicator', {
    'to-be-deleted': isMarkedForDeletion,
    'to-be-added': isMarkedForAddition,
  })

  const tooltip = isMarkedForDeletion
    ? strings.add_switch_back
    : strings.remove_switch

  const handleIconClick = () => {
    setIsTooltipVisible(false)
    handleClick(panelNumber)
  }

  return (
    <>
      <div className={stateIndicatorClassNames} />
      <ChecTooltip
        title={tooltip}
        placement="right"
        overlayStyle={{ zIndex: 10001 }}
        visible={isTooltipVisible}
        onVisibleChange={setIsTooltipVisible}
        childWrapStyle={{ display: 'inline-flex', cursor: 'pointer' }}
      >
        <Icon {...iconProps()} onClick={handleIconClick} />
      </ChecTooltip>
    </>
  )
}

AddRemoveCell.propTypes = {
  handleClick: PropTypes.func,
  isMarkedForDeletion: PropTypes.bool,
  isMarkedForAddition: PropTypes.bool,
  panelNumber: PropTypes.string,
}

const Blurb = styled.div`
  margin: 0 0 16px 0;
  font-family: Arial, sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px;
  letter-spacing: normal;
  text-align: left;
  color: rgba(7, 7, 15, 0.87);
`
